/** Bell with the unread count; the dropdown lists the latest notifications by day, filtered by tab. Opening one marks it read. */
import { useEffect, useRef, useState } from 'react';
import type { AppNotification } from '@platform/shared';
import { Link } from '../lib/router';
import { markAllRead, openNotification, useNotifications, useUnreadCount } from '../stores/notifications';
import { ctaLabel, dayGroup, Glyph, NotificationIcon, notificationMeta, timeLabel } from './NotificationIcon';
import { cx, Icon } from './ui';

type Tab = 'all' | 'account' | 'trading';
const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'account', label: 'Account' },
  { id: 'trading', label: 'Trading' },
];

export const inTab = (n: AppNotification, tab: Tab | 'unread') =>
  tab === 'all' ? true : tab === 'unread' ? !n.read : notificationMeta(n).tab === tab;

/** Today first, then Earlier; empty groups left out. */
export function byDay(items: AppNotification[]) {
  return (['Today', 'Earlier'] as const).map((label) => ({ label, items: items.filter((n) => dayGroup(n.createdAt) === label) })).filter((g) => g.items.length);
}

export function NotificationBell({ className, buttonClassName }: { className?: string; buttonClassName?: string }) {
  const items = useNotifications();
  const unread = useUnreadCount();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('all');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const go = (n: AppNotification) => {
    setOpen(false);
    openNotification(n);
  };
  const counts = (t: Tab) => items.filter((n) => !n.read && inTab(n, t)).length;
  const groups = byDay(items.filter((n) => inTab(n, tab)));

  return (
    <div ref={ref} className={cx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
        className={cx('relative grid size-[38px] place-items-center rounded-full text-nav-fg hover:bg-nav-pill', buttonClassName)}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span className="num absolute -top-0.5 -right-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-nav-fg px-1 text-[10.5px] leading-none font-semibold text-nav ring-2 ring-nav">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <section
          aria-label="Notifications"
          className="shadow-pop absolute top-12 right-0 z-40 flex max-h-[min(640px,80vh)] w-[min(404px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-float-line bg-float text-fg"
        >
          <div className="flex items-center justify-between gap-3 pt-4 pr-3 pb-3 pl-[18px]">
            <div className="flex items-center gap-2">
              <h2 className="m-0 text-[15px] font-semibold">Notifications</h2>
              {unread > 0 && <span className="num rounded-full bg-raised px-[7px] py-0.5 text-[11px] font-medium">{unread} new</span>}
            </div>
            <button
              type="button"
              onClick={() => void markAllRead()}
              disabled={!unread}
              className="rounded-lg px-2 py-2 text-[12.5px] font-medium text-fg hover:bg-raised disabled:text-muted disabled:hover:bg-transparent"
            >
              Mark all as read
            </button>
          </div>
          <div role="tablist" aria-label="Filter notifications" className="mx-4 mb-2 flex gap-1 rounded-[10px] bg-track p-[3px]">
            {TABS.map((t) => {
              const c = counts(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={cx(
                    'flex min-h-[34px] flex-1 items-center justify-center gap-1.5 rounded-lg text-[12.5px]',
                    tab === t.id ? 'bg-float-on font-semibold text-fg shadow-sm' : 'font-medium text-muted hover:text-fg',
                  )}
                >
                  {t.label}
                  {c > 0 && <span className="num text-[10.5px] text-muted">{c}</span>}
                </button>
              );
            })}
          </div>
          <div className="min-h-0 overflow-y-auto px-1.5 pb-1.5">
            {groups.map((g) => (
              <div key={g.label}>
                <h3 className="m-0 px-3 pt-3 pb-1.5 text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{g.label}</h3>
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                  {g.items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => go(n)}
                        className={cx('flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-raised', !n.read && 'bg-fg/[0.035]')}
                      >
                        <NotificationIcon n={n} size="sm" />
                        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                          <span className="flex items-baseline gap-2.5">
                            <span className={cx('min-w-0 text-[13.5px] leading-snug', n.read ? 'font-medium' : 'font-semibold')}>
                              {n.title}
                              {!n.read && <span className="sr-only"> (unread)</span>}
                            </span>
                            <span className="num ml-auto shrink-0 text-[11px] text-muted">{timeLabel(n.createdAt)}</span>
                          </span>
                          {n.body && <span className="line-clamp-2 text-[12.5px] leading-[1.45] text-muted">{n.body}</span>}
                          {n.link && (
                            <span className="mt-[3px] inline-flex items-center gap-1 text-[12.5px] font-semibold">
                              {ctaLabel(n)}
                              <Icon name="chevron" size={13} strokeWidth={2.4} className="-rotate-90" />
                            </span>
                          )}
                        </span>
                        <span aria-hidden="true" className={cx('mt-1.5 size-2 shrink-0 rounded-full', n.read ? 'bg-transparent' : 'bg-fg')} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {!groups.length && (
              <div className="flex flex-col items-center gap-2.5 px-6 pt-12 pb-[52px] text-center">
                <span aria-hidden="true" className="grid size-11 place-items-center rounded-full bg-raised">
                  <Glyph name="check" />
                </span>
                <div className="text-[14px] font-semibold">You’re all caught up</div>
                <div className="text-[12.5px] text-muted">New deposits, alerts and account updates will appear here.</div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-line px-2.5 py-1.5">
            <Link
              to="/account/notifications/settings"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-[7px] rounded-lg px-2 py-2.5 text-[12.5px] font-medium text-muted no-underline hover:text-fg"
            >
              <Icon name="settings" size={15} />
              Notification settings
            </Link>
            <Link to="/account/notifications" onClick={() => setOpen(false)} className="rounded-lg px-2 py-2.5 text-[12.5px] font-semibold text-fg no-underline">
              View all
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
